import React, { useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";
import StoreFormContainer from "components/store-form-container";
import { checkIfTakebackIsFullKiosk, getQueryParams } from "lib/utils";
import { Header } from "components/header";
import { useTranslation } from "react-i18next";
import { useAddStatus } from "../../api/tes/useAddStatus";
import { useQuery } from "react-query";
import AuthContext from "../../contexts/AuthContext";
import ExternalLink from "../../assets/icons/external_link.svg";

export default function DropoffPrepWeighing() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const { authTokens } = useContext(AuthContext);
  const { mutateAsync: addStatus } = useAddStatus();
  const pollingIntervalRef = useRef(null);

  // Custom query with polling for takeback data
  const {
    data: takebackData,
    refetch,
    isLoading,
  } = useQuery(
    ["takeback", queryParams.id],
    async () => {
      const freshTokens = JSON.parse(localStorage.getItem("authTokens"));
      const response = await fetch(
        `${process.env.REACT_APP_API}/takeback/${queryParams.id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshTokens?.access_token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch takeback");
      }
      const data = await response.json();
      return data?.[0];
    },
    {
      enabled: Boolean(queryParams.id && authTokens?.access_token),
      refetchOnWindowFocus: false,
    }
  );

  const takeback = takebackData;
  const isFullKiosk = !isLoading && checkIfTakebackIsFullKiosk(takeback);

  const { t } = useTranslation();

  // Check if seller has accepted terms and conditions
  const hasSellerAccepted =
    takeback?.rawTakeback?.takeback_processing_history_list?.some(
      (status) => status.status === "seller-accepted"
    );

  // Start polling when seller hasn't accepted, stop when they have
  useEffect(() => {
    if (!hasSellerAccepted && queryParams.id) {
      // Start polling every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        refetch();
      }, 1000);
    } else {
      // Stop polling if seller has accepted or no ID
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [hasSellerAccepted, queryParams.id, refetch]);

  // Add checked-in status when component loads (associate starts processing)
  useEffect(() => {
    if (takeback && queryParams.id) {
      const addCheckedInStatus = async () => {
        try {
          // Check if already checked-in to avoid duplicate status
          const alreadyCheckedIn =
            takeback.rawTakeback?.takeback_processing_history_list?.some(
              (status) => status.status === "in-store-checkin"
            );

          if (!alreadyCheckedIn) {
            await addStatus({
              id: queryParams.id,
              input: {
                status: "in-store-checkin",
                parent_status: "processing",
                updated_at: parseInt(Date.now() / 1000),
              },
            });
          }
        } catch (error) {
          console.error("Failed to add checked-in status:", error);
        }
      };

      addCheckedInStatus();
    }
  }, [takeback, queryParams.id, addStatus]);

  const handleSubmit = () => {
    !isFullKiosk
      ? navigate({
          pathname: `/en-US/store/trade-in-scan-weight`,
          search: createSearchParams(queryParams).toString(),
        })
      : navigate({
          pathname: t("urls.store_trade_in_capture_weight"),
          search: createSearchParams(queryParams).toString(),
        });
  };

  let guidenceUrl = "/en-US/preparation-guidelines";

  if (takebackData) {
    const state = (takebackData.u_lego_return_type || "")
      .slice(-2)
      .toUpperCase();
    guidenceUrl = `${guidenceUrl}?state=${state}&preselected=in-store`;
  }

  return (
    <StoreFormContainer
      submitLabel="Continue"
      secondaryLabel="Back"
      secondaryHref={t("urls.store_check_in")}
      secondaryParams={queryParams}
      onSubmit={handleSubmit}
      submitDisabled={!hasSellerAccepted}
    >
      <Header />
      <div className="flex items-center justify-center h-full flex-grow py-4">
        <div className="flex flex-col items-center">
          {!hasSellerAccepted ? (
            <div className="w-full max-w-2xl mb-6 p-4">
              <h2 className="font-semibold text-4xl text-center mb-0">
                Waiting for seller to accept <br /> the Terms and Conditions
              </h2>

              <p className="text-xl text-gray-600 text-center mb-8 pt-[16px]">
                Please let the seller know a link to accept <br />
                was sent to their email.
              </p>

              <p
                className={`text-center mb-8 pt-[16px] ${!takeback?.number && "opacity-0"} transition-opacity duration-200`}
              >
                <span className="font-semibold">Trade-in ID:</span>{" "}
                {takeback?.number}
              </p>
            </div>
          ) : (
            <>
              {!isFullKiosk ? (
                <>
                  <h2 className="font-semibold text-4xl text-center mb-10">
                    Prepare the trade-in for weighing
                  </h2>

                  <div className="w-full max-w-2xl space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        1
                      </div>
                      <p className="text-lg">
                        Open the package and check for any items that don't meet
                        the requirements.
                      </p>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        2
                      </div>
                      <p className="text-lg">
                        Kindly ask the seller to remove and take home any
                        unaccepted items.
                      </p>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        3
                      </div>
                      <p className="text-lg">
                        Make sure the scales are on and set to zero.
                      </p>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        4
                      </div>
                      <p className="text-lg">
                        Have the seller tap <b>"Start"</b> on the kiosk screen,
                        and place the package on the scale to weigh it.
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-semibold text-4xl text-center mb-0">
                    Verify and prepare for weighing
                  </h2>

                  <p className="text-xl text-gray-600 text-center mb-8">
                    Check for accepted items
                  </p>
                  <div className="w-full max-w-2xl space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        1
                      </div>
                      <p className="text-lg">
                        Prompt the seller to start the trade-in process at the
                        kiosk.
                      </p>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        2
                      </div>
                      <p className="text-lg">
                        Ask the seller to pour their LEGO bricks into the tray,
                        just enough to be able to sort through them accurately.
                        Once those have been reviewed, they can add more bricks
                        as needed.
                      </p>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                        3
                      </div>
                      <p className="text-lg">
                        While the tray is being filled, visually check for
                        unaccepted items, remove them, and inform the seller
                        they won't count in the reward.
                      </p>
                    </div>
                    <p>
                      <a
                        className="text-[#006CB7] flex items-end"
                        href={guidenceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="pt-1 inline-block relative top-[2px]">
                          View accepted items guidance
                        </span>
                        <img
                          className="ml-2 inline-block"
                          src={ExternalLink}
                          alt="External Link"
                        />
                      </a>
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </StoreFormContainer>
  );
}
